import styles from "./DatePicker.module.css"
import React, { useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { ChevronUp } from 'lucide-react';
import { Calendar }  from 'lucide-react' ;
import CalendarComponent from "../../components/Calendar/Calendar";
import { getDateISO, formatDate } from "../../utils/calendar";
import { useTranslation } from "react-i18next";
import { useDropdown } from "../../hooks/useDropdown";
import { useSchedule } from "../../context/ScheduleContext";
/**
* Props for the DatePicker component.
*/ 
interface DatePickerProps {
    text?: string, // Datepicker label
    onChange?: (value: string[]) => void, // Change event handler
    selectedDates?: string[], // Selected dates
    globalDates?: string[], // Globally selected dates
    className? :string, // Optional additional class
    wrapperClassName?: string, // Optional additional class
    calendarClassName?: string, // Optional calendar class
    showOnly?: boolean // Whether to make the datepicker show only
    single?: boolean // Whether the datepicker should allow only one date
    placeholder?: string // Optional custom placeholder
}
/**
* A datepicker component with optional label and multiple customization options.
*
* @param props DatePicker props
* @param {string} props.text - Datepicker label
* @param {(value: string[]) => void} props.onChange - Change event handler
* @param {string[]} props.selectedDates - Selected dates
* @param {string[]} props.globalDates - Selected dates in order context
* @param {string} props.className - Optional additional class
* @param {string} props.wrapperClassName - Optional additional class
* @param {string} props.calendarClassName - Optional calendar class
* @param {boolean} props.showOnly - Whether to make the datepicker show only
* @param {boolean} props.single - Whether the datepicker should allow only one date
* @param {string} props.placeholder - Optional custom placeholder
* @returns TSX.Element
*/
const DatePicker: React.FC<DatePickerProps> = ({ text, showOnly = false, onChange, selectedDates = [], className, wrapperClassName, calendarClassName, globalDates, single = false, placeholder = ""}) => {
    const { t } = useTranslation(); // Translation
    const { ref, isOpen, setIsOpen } = useDropdown(); // Dropdown hook
    const { fetchSchedule } = useSchedule();
    if(placeholder === ""){
        placeholder = t("inputs.date_placeholder")
    }
    useEffect(() => {
        if (!localStorage.getItem("catermat_scheduleFetchedAt")) {
            fetchSchedule();
        }
    },[isOpen])
    // Handle selected dates
    const handleDateSelection = (date: Date | null) => {
        const newDate = date ? getDateISO(date) : null;
        if (newDate) {
            // Handle selecting only one date
            if(single === true && onChange){
                onChange([newDate]);
                return;
            }
            // Create new list
            let newSelectedDates: string[] = [];
            // If date is clicked twice deselect it
            if (selectedDates && selectedDates.includes(newDate)) {
                newSelectedDates = selectedDates.filter(date => date !== newDate).sort();
            } else {
                // Make sure there are at max 3 selected dates
                if (selectedDates && selectedDates.length >14) {
                    newSelectedDates = [...selectedDates.slice(-14), newDate].sort();
                } else {
                    newSelectedDates = [...(selectedDates || []), newDate].sort();
                }
            }
            if(onChange){
                onChange(newSelectedDates);
            }
        }
    }
    // Show only mode
    if(showOnly === true){
        return(
            <>
                <div className={wrapperClassName? `${styles.DatePickerBlock} ${wrapperClassName}` : `${styles.DatePickerBlock}`} ref={ref}>
                    <div className={className? `${styles.DatePickerCenter} ${className}` : `${styles.DatePickerCenter}`} onClick={()=>setIsOpen(!isOpen)}>
                        <div className={styles.DatePickerIcon}>
                            <Calendar />
                        </div>
                        <div className={styles.SelectedDate}><p>{selectedDates.length > 0 ? selectedDates.slice(0,3).map(date => formatDate(date)).join(" | ") : placeholder}{selectedDates.length > 3 ?" |..." : ""}</p></div>
                    </div>
                    {text && <div className={styles.DatePickerLabel}><p>{text}</p></div>}
                </div>
            </>
        )
    }
    return(
        <>
            <div className={wrapperClassName? `${styles.DatePickerBlock} ${wrapperClassName}` : `${styles.DatePickerBlock}`} ref={ref}>
                <div className={className? `${styles.DatePickerCenter} ${className}` : `${styles.DatePickerCenter}`} onClick={()=>setIsOpen(!isOpen)}>
                    <div className={styles.DatePickerIcon}>
                        <Calendar />
                    </div>
                    <div className={styles.SelectedDate}><p>{selectedDates.length > 0 ? selectedDates.slice(0,3).map(date => formatDate(date)).join(" | ") : placeholder}{selectedDates.length > 3 ?" |..." : ""}</p></div>
                    <button type="button" className={styles.DatePickerDropdownButton} >
                        {isOpen ? <ChevronUp /> : <ChevronDown />}
                    </button>
                </div>
                {text && <div className={styles.DatePickerLabel}><p>{text}</p></div>}
                {isOpen && (
                    <CalendarComponent
                        date={new Date()}
                        onDateChanged={handleDateSelection}
                        selectedDates={selectedDates}
                        globalDates={globalDates ? globalDates : []}
                        className={calendarClassName}
                    />
                )}
            </div>
        </>
    )
};

export default DatePicker;